import { Fragment, useEffect, useMemo, useState } from 'react'
import useAuth from '../../hooks/useAuth';
import IsAuth from '../../utillityComponents/IsAuth'

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

export default function EventParticipantList(props) {
  const { searchTerm } = useAuth();
  const [ activeList, setActiveList ] = useState([]);

  const [ openCreateMenu, setOpenCreateMenu ] = useState(false)
  const [ sponsorFilter, setSponsorFilter ] = useState('alle') // alle | uden | med
  const [ sponsorView, setSponsorView ] = useState('inline') // inline | accordion
  const [ expanded, setExpanded ] = useState(new Set())
  const [ sortBy, setSortBy ] = useState('startnr') // startnr | navn | kr_km | kr_fast | antal_sponsorer
  const [ sortDir, setSortDir ] = useState('asc') // asc | desc

  const toggleExpanded = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  // Load persisted preferences on mount
  useEffect(() => {
    const savedFilter = localStorage.getItem('event_participant-sponsorFilter')
    const savedView = localStorage.getItem('event_participant-sponsorView')
    const savedSortBy = localStorage.getItem('event_participant-sortBy')
    const savedSortDir = localStorage.getItem('event_participant-sortDir')
    if (savedFilter) setSponsorFilter(savedFilter)
    if (savedView) setSponsorView(savedView)
    if (savedSortBy) setSortBy(savedSortBy)
    if (savedSortDir) setSortDir(savedSortDir)
  }, [])

  // Persist preferences when they change
  useEffect(() => {
    localStorage.setItem('event_participant-sponsorFilter', sponsorFilter)
  }, [sponsorFilter])

  useEffect(() => {
    localStorage.setItem('event_participant-sponsorView', sponsorView)
  }, [sponsorView])

  useEffect(() => {
    localStorage.setItem('event_participant-sortBy', sortBy)
  }, [sortBy])

  useEffect(() => {
    localStorage.setItem('event_participant-sortDir', sortDir)
  }, [sortDir])

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm && searchTerm !== '' && searchTerm.length > 1) {
        let searchTermSrape = searchTerm.toLowerCase()
        const filteredItems = props.participants.filter(participants => {
            return (
            participants.name.toLowerCase().includes(searchTermSrape) ||
            participants.startnr.toLowerCase().includes(searchTermSrape)
            )});   
        setActiveList(filteredItems);
      } else {
        setActiveList(props.participants);
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [searchTerm, props.participants]);

  const filteredParticipants = useMemo(() => {
    if (!activeList) return []
    if (sponsorFilter === 'alle') return activeList
    if (sponsorFilter === 'uden') return activeList.filter((p) => (p.sponsor || []).length === 0)
    if (sponsorFilter === 'med') return activeList.filter((p) => (p.sponsor || []).length > 0)
    return activeList
  }, [activeList, sponsorFilter])

  const sortedParticipants = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1
    return [...filteredParticipants].sort((a, b) => {
      if (sortBy === 'startnr') {
        return (parseInt(a.startnr, 10) - parseInt(b.startnr, 10)) * dir
      }
      if (sortBy === 'navn') {
        return a.name.localeCompare(b.name, 'da') * dir
      }
      if (sortBy === 'kr_km') {
        return (a.donation_km - b.donation_km) * dir
      }
      if (sortBy === 'kr_fast') {
        return (a.total_donation_fixed - b.total_donation_fixed) * dir
      }
      if (sortBy === 'antal_sponsorer') {
        return ((a.sponsor || []).length - (b.sponsor || []).length) * dir
      }
      return 0
    })
  }, [filteredParticipants, sortBy, sortDir])
  
  return (
      <>
    <h2 className="max-w-6xl mx-auto mt-8 px-4 text-lg leading-6 font-medium text-gray-900 sm:px-6 lg:px-8">
    {props.title}
  </h2>
    <div className="px-4 sm:px-6 lg:px-8">
    <br></br>
      {IsAuth(['Admin', 'Ngo', 'Org_admin', 'Event_admin']) ?
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative inline-block text-left">
            <button
              type="button"
              onClick={() => setOpenCreateMenu((v) => !v)}
              className="inline-flex w-full justify-center gap-x-1.5 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 focus:outline-none"
            >
              Tilføj
              <svg className="-mr-1 h-5 w-5 text-white" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.08 1.04l-4.25 4.25a.75.75 0 01-1.06 0L5.25 8.27a.75.75 0 01-.02-1.06z" clipRule="evenodd" />
              </svg>
            </button>
            {openCreateMenu ? (
              <div className="absolute left-0 z-10 mt-2 w-48 origin-top-left rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                <div className="py-1">
                  <button
                    type="button"
                    className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                    onClick={() => { setOpenCreateMenu(false); props.openPopupParticipant && props.openPopupParticipant(); }}
                  >
                    Deltager
                  </button>
                  <button
                    type="button"
                    className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                    onClick={() => { setOpenCreateMenu(false); props.openPopupSponsor && props.openPopupSponsor(); }}
                  >
                    Sponsor
                  </button>
                </div>
              </div>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <label htmlFor="sponsorFilter" className="text-sm text-gray-700">Filtrer</label>
              <select
                id="sponsorFilter"
                name="sponsorFilter"
                value={sponsorFilter}
                onChange={(e) => setSponsorFilter(e.target.value)}
                className="block w-44 rounded-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 text-sm"
              >
                <option value="alle">Alle løbere</option>
                <option value="uden">Uden sponsorer</option>
                <option value="med">Med sponsorer</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label htmlFor="sponsorView" className="text-sm text-gray-700">Visning</label>
              <select
                id="sponsorView"
                name="sponsorView"
                value={sponsorView}
                onChange={(e) => setSponsorView(e.target.value)}
                className="block w-40 rounded-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 text-sm"
              >
                <option value="inline">Vis sponsorer</option>
                <option value="accordion">Skjul som accordion</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label htmlFor="sortBy" className="text-sm text-gray-700">Sorter efter</label>
              <select
                id="sortBy"
                name="sortBy"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="block w-44 rounded-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 text-sm"
              >
                <option value="startnr">Startnummer</option>
                <option value="navn">Navn</option>
                <option value="kr_km">kr/km</option>
                <option value="kr_fast">kr/fast</option>
                <option value="antal_sponsorer">Antal sponsorer</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label htmlFor="sortDir" className="text-sm text-gray-700">Retning</label>
              <select
                id="sortDir"
                name="sortDir"
                value={sortDir}
                onChange={(e) => setSortDir(e.target.value)}
                className="block w-32 rounded-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 text-sm"
              >
                <option value="asc">Stigende</option>
                <option value="desc">Faldende</option>
              </select>
            </div>
          </div>
        </div>
        : ''
      }
      <div className="mt-8 flex flex-col">
        <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              <table className="min-w-full">
                <thead className="bg-white">
                  <tr>
                    <th scope="col" className="py-3.5 pl-0 pr-3 text-center text-sm font-semibold text-gray-900 sm:pl-0">
                      Navn
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-center text-sm font-semibold text-gray-900">
                      kr/km
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-center text-sm font-semibold text-gray-900">
                      Fast Donation
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-center text-sm font-semibold text-gray-900">
                      Total kr/km
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-center text-sm font-semibold text-gray-900">
                      Total Donationer
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-center text-sm font-semibold text-gray-900">
                      Betalingtype
                    </th>
                    <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                      <span className="sr-only">Rediger</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {sortedParticipants.map((participant) => (
                    <Fragment key={participant.name}>
                      <tr
                      key={participant.id} 
                      className="border-t border-gray-200">
                        <th
                          colSpan={5}
                          scope="colgroup"
                          className="bg-gray-50 px-4 py-2 text-left text-sm font-semibold text-gray-900 sm:px-6"
                        >
                           <img
                            className="inline-block h-9 w-9 rounded-full ml-4 mr-5"
                            src={participant.img + "/36x36"}
                            alt={participant.name}
                          />
                          {participant.startnr + ' - ' + participant.name}  --  {new Intl.NumberFormat('da-DK', {maximumFractionDigits: 0}).format(participant.donation_km)} kr/km   --  {new Intl.NumberFormat('da-DK', {maximumFractionDigits: 0}).format(participant.total_donation_fixed)} kr/fast  --  {(participant.sponsor || []).length} sponsorer
                          {participant.phone_number ? (
                            <span className="ml-3 font-normal text-gray-500">
                              Tlf. <a href={'tel:' + participant.phone_number} className="hover:text-indigo-600">{participant.phone_number}</a>
                            </span>
                          ) : null}
                        </th>
                        <th colSpan={1} scope="colgroup" className="bg-gray-50 px-4 py-2 text-left text-sm font-semibold text-gray-900 sm:px-6"></th>
                        <th colSpan={2} scope="colgroup" className="bg-gray-50 px-4 py-2 text-left text-sm font-semibold text-gray-900 sm:px-6">
                        <div className="flex items-center justify-end gap-4">
                          {sponsorView === 'accordion' ? (
                            <button
                              type="button"
                              className="text-indigo-600 hover:text-indigo-900"
                              onClick={() => toggleExpanded(participant.id)}
                            >
                              {expanded.has(participant.id) ? 'Skjul sponsorer' : `Vis sponsorer (${(participant.sponsor || []).length})`}
                            </button>
                          ) : null}
                          {IsAuth(['Admin', 'Ngo', 'Org_admin', 'Event_admin']) ?
                            <button
                              key={participant.id + "_edit"}
                                type="button"
                                className= "text-indigo-600 hover:text-indigo-900"
                                onClick= {() => props.openPopupUpdateParticipant(participant.slug)}
                              >
                                Edit
                              </button>
                              : ""                       
                            }
                        </div>
                        </th>
                      </tr>
                      {(sponsorView === 'inline' || expanded.has(participant.id)) && participant.sponsor.map((sponsor, eventsIdx) => (
                        <tr
                          key={sponsor.id}
                          className={classNames(eventsIdx === 0 ? 'border-gray-300' : 'border-gray-200', 'border-t')}
                        >
                          <td className="whitespace-nowrap py-4 pl-0 pr-3 text-sm font-medium text-gray-900 sm:pl-0">
                            {sponsor.first_name}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{new Intl.NumberFormat('da-DK', {maximumFractionDigits: 0}).format(sponsor.donation_km)} kr</td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{new Intl.NumberFormat('da-DK', {maximumFractionDigits: 0}).format(sponsor.donation_fixed)} kr</td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{new Intl.NumberFormat('da-DK', {maximumFractionDigits: 0}).format(sponsor.total_donation_km)} kr</td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{new Intl.NumberFormat('da-DK', {maximumFractionDigits: 0}).format(sponsor.total_donation)} kr</td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{sponsor.payment_type == 'mobilepay' ? 'Mobilepay' : sponsor.payment_type == 'transfer' ? 'Overførsel' : sponsor.payment_type == 'credit_card' ? 'Kreditkort' : ''}</td>
                          <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                            <button
                            key={sponsor.id + "_edit"}
                              type="button"
                              className= "text-indigo-600 hover:text-indigo-900"
                              onClick= {() => props.openPopupUpdateSponsor(sponsor.id)}
                            >
                              Edit
                            </button>                            
                          </td>
                        </tr>
                      ))}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  )
}